import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import Page from 'src/components/Page';
import {
  BRAND,
  C,
  cancelButton,
  neutralButton,
  primaryButton
} from '../../components/HavcOffersComponents/teklifTheme';
import {
  fiyatListesiErisimKaydet,
  fiyatListesiKalemleriKaydet,
  fiyatListesiKaydet,
  getFiyatListeleri,
  getFiyatListesiErisim,
  getFiyatListesiKalemleri
} from '../../components/HavcOffersComponents/fiyatListesiApi';
import { getLookuplar } from '../../components/HavcOffersComponents/teklifApi';
import {
  BIRIMLER,
  GRUPLAR,
  PARA_BIRIMLERI,
  blobIndir,
  dosyaOku,
  excelOku,
  excelOlustur,
  kalemAnahtari,
  karsilastir,
  katla,
  trFiltre,
  trFormat,
  trSayi
} from '../../components/HavcOffersComponents/fiyatListesiExcel';

// ==============================|| STILLER ||==============================

const OK = '#15803d';
const ERR = '#c62828';
const COLS = '28px minmax(220px, 1fr) 84px 118px 132px 112px 64px 40px';

const styles = {
  root: (theme) => ({
    padding: theme.spacing(3),
    backgroundColor: C.canvas,
    minHeight: '100vh',
    paddingBottom: theme.spacing(12),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(12)
    }
  }),
  hero: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mb: 2.5,
    flexWrap: 'wrap'
  },
  heroIcon: {
    width: 52,
    height: 52,
    borderRadius: '16px',
    display: 'grid',
    placeItems: 'center',
    color: '#ffffff',
    flexShrink: 0,
    background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.hover} 100%)`,
    boxShadow: `0 8px 20px -6px ${BRAND.main}8c`
  },
  card: {
    borderRadius: '16px',
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    overflow: 'hidden'
  },
  listeBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1.5,
    p: 2.5,
    borderBottom: `1px solid ${C.borderSoft}`
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    py: 1.75,
    borderBottom: `1px solid ${C.borderSoft}`
  },
  outlinedButton: {
    height: 40,
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 600,
    color: `${C.inkBody} !important`,
    borderColor: `${C.border} !important`,
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: `${C.inkSoft} !important`,
      backgroundColor: C.headBg
    }
  },
  colHead: {
    display: { xs: 'none', md: 'grid' },
    gridTemplateColumns: COLS,
    gap: '10px',
    px: 2.5,
    py: 1.25,
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${C.borderSoft}`
  },
  section: {
    border: `1px solid ${C.border}`,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: C.surface
  },
  secHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.75,
    py: 1.25,
    backgroundColor: C.headBg
  },
  secIcon: {
    width: 34,
    height: 34,
    borderRadius: '10px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: `${BRAND.main}1f`,
    color: BRAND.main
  },
  row: (durum) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: { xs: '28px 1fr 1fr 40px', md: COLS },
    gridTemplateAreas: {
      xs: '"h t t x" ". a b s" ". f p s"',
      md: '"h t a b f p s x"'
    },
    gap: '8px 10px',
    alignItems: 'center',
    px: 1.75,
    py: 1,
    borderTop: `1px solid ${C.borderSoft}`,
    backgroundColor: durum === 'silinecek' ? '#fdecec' : 'transparent',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: '0 3px 3px 0',
      backgroundColor:
        durum === 'yeni' ? OK : durum === 'degisti' ? BRAND.main : 'transparent'
    }
  }),
  input: (hata) => ({
    width: '100%',
    height: 36,
    px: 1.25,
    borderRadius: '8px',
    border: `1px solid ${hata ? ERR : C.border}`,
    backgroundColor: C.surface,
    color: C.ink,
    font: 'inherit',
    fontSize: '0.875rem',
    minWidth: 0,
    boxShadow: hata ? `0 0 0 3px ${ERR}1f` : 'none',
    '&:hover:not(:disabled)': { borderColor: hata ? ERR : C.inkSoft },
    '&:focus': {
      outline: 'none',
      borderColor: BRAND.main,
      boxShadow: `0 0 0 3px ${BRAND.main}40`
    },
    '&:disabled': {
      backgroundColor: C.headBg,
      color: C.inkBody,
      cursor: 'default'
    }
  }),
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  // Üst menünün (AppBar, 64px) hemen altında
  saveBar: {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    top: { xs: 64, sm: 76 },
    animation: 'saveBarGir .2s ease-out',
    '@keyframes saveBarGir': {
      from: { opacity: 0, transform: 'translate(-50%, -12px)' },
      to: { opacity: 1, transform: 'translate(-50%, 0)' }
    },
    width: 'min(720px, calc(100% - 32px))',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    py: 1.25,
    pl: 2.25,
    pr: 1.25,
    borderRadius: '14px',
    backgroundColor: C.ink,
    color: '#ffffff',
    boxShadow: '0 18px 40px -16px rgba(15,23,42,0.6)',
    zIndex: 1200
  },
  pill: (bg, fg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    height: 22,
    px: 1,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    backgroundColor: bg,
    color: fg
  })
};

const GRUP_IKON = {
  MALZEME: <Inventory2OutlinedIcon fontSize="small" />,
  ISCILIK: <EngineeringOutlinedIcon fontSize="small" />,
  DIGER: <MoreHorizIcon fontSize="small" />
};

const DURUM_ETIKET = {
  yeni: ['Yeni', '#e8f5e9', OK],
  guncel: ['Güncellenecek', `${BRAND.main}1f`, '#b45309'],
  ayni: ['Değişiklik yok', C.borderSoft, C.inkMuted],
  hata: ['Hatalı', '#fdecec', ERR],
  pasif: ['Pasife alınacak', '#fef6dc', '#a16207']
};

// ==============================|| YARDIMCILAR ||==============================

let geciciSayac = 0;
const yeniAnahtar = () => `y${Date.now()}_${(geciciSayac += 1)}`;

const normalize = (k) => ({
  _anahtar: `k${k.KalemId}`,
  KalemId: k.KalemId,
  Grup: k.Grup,
  Tanim: k.Tanim || '',
  Adet: Number(k.Adet),
  Birim: k.Birim,
  Fiyat: Number(k.Fiyat),
  ParaBirimi: k.ParaBirimi,
  Aktif: k.Aktif !== false,
  Sira: k.Sira || 0
});

const ALANLAR = [
  'Grup',
  'Tanim',
  'Adet',
  'Birim',
  'Fiyat',
  'ParaBirimi',
  'Aktif'
];

const sirala = (liste) =>
  GRUPLAR.flatMap((g) => liste.filter((k) => k.Grup === g.kod));

const dosyaAdiTemizle = (ad) =>
  String(ad || 'FiyatListesi')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .trim();

// ==============================|| SATIR ||==============================

const KalemSatiri = memo(function KalemSatiri({
  kalem,
  durum,
  hata,
  duzenleyebilir,
  onDegis,
  onSil,
  onSurukleBasla,
  onSurukleUstunde,
  onBirak
}) {
  const [tanim, setTanim] = useState(kalem.Tanim);
  const [adet, setAdet] = useState(String(kalem.Adet).replace('.', ','));
  const [fiyat, setFiyat] = useState(trFormat(kalem.Fiyat));

  useEffect(() => setTanim(kalem.Tanim), [kalem.Tanim]);
  useEffect(
    () =>
      setAdet(
        Number.isNaN(Number(kalem.Adet))
          ? String(kalem.Adet)
          : String(kalem.Adet).replace('.', ',')
      ),
    [kalem.Adet]
  );
  useEffect(
    () =>
      setFiyat(
        Number.isNaN(Number(kalem.Fiyat))
          ? String(kalem.Fiyat)
          : trFormat(kalem.Fiyat)
      ),
    [kalem.Fiyat]
  );

  const a = kalem._anahtar;
  const sayiYaz = (alan, metin) => {
    const n = trSayi(metin);
    onDegis(a, alan, Number.isNaN(n) ? metin : n);
  };
  const silinecek = durum === 'silinecek';
  const kilitli = !duzenleyebilir || silinecek;
  const soluk = !kalem.Aktif ? { color: C.inkSoft } : {};

  return (
    <Box
      sx={styles.row(durum)}
      onDragOver={(e) => onSurukleUstunde(e, kalem)}
      onDrop={(e) => onBirak(e, kalem)}
    >
      <Box
        sx={{
          gridArea: 'h',
          display: 'grid',
          placeItems: 'center',
          height: 34,
          color: C.inkSoft,
          cursor: duzenleyebilir ? 'grab' : 'default'
        }}
        draggable={duzenleyebilir}
        onDragStart={(e) => onSurukleBasla(e, kalem)}
        aria-label="Sürükleyerek sırala"
      >
        {duzenleyebilir && <DragIndicatorIcon fontSize="small" />}
      </Box>

      <Box sx={{ gridArea: 't', minWidth: 0 }}>
        <Box
          component="input"
          sx={{
            ...styles.input(hata?.Tanim),
            ...soluk,
            textDecoration: silinecek ? 'line-through' : 'none'
          }}
          value={tanim}
          maxLength={250}
          placeholder="Kalem tanımı"
          aria-label="Malzeme tanımı"
          disabled={kilitli}
          onChange={(e) => setTanim(e.target.value)}
          onBlur={() =>
            tanim.trim() !== kalem.Tanim && onDegis(a, 'Tanim', tanim.trim())
          }
        />
        {hata?.Tanim && (
          <Typography sx={{ color: ERR, fontSize: '0.75rem', mt: 0.25 }}>
            {hata.Tanim}
          </Typography>
        )}
      </Box>

      <Box
        component="input"
        sx={{
          gridArea: 'a',
          ...styles.input(hata?.Adet),
          ...styles.num,
          ...soluk
        }}
        inputMode="decimal"
        value={adet}
        aria-label="Adet"
        title={hata?.Adet || 'Teklife eklenince gelen varsayılan miktar'}
        disabled={kilitli}
        onChange={(e) => setAdet(e.target.value)}
        onBlur={() => sayiYaz('Adet', adet)}
      />

      <Box
        component="select"
        sx={{
          gridArea: 'b',
          ...styles.input(false),
          ...soluk,
          cursor: 'pointer'
        }}
        value={kalem.Birim}
        aria-label="Birim"
        disabled={kilitli}
        onChange={(e) => onDegis(a, 'Birim', e.target.value)}
      >
        {(BIRIMLER.includes(kalem.Birim)
          ? BIRIMLER
          : [kalem.Birim, ...BIRIMLER]
        ).map((b) => (
          <option key={b} value={b}>
            {b}
          </option>
        ))}
      </Box>

      <Box
        component="input"
        sx={{
          gridArea: 'f',
          ...styles.input(hata?.Fiyat),
          ...styles.num,
          ...soluk
        }}
        inputMode="decimal"
        value={fiyat}
        placeholder="0,00"
        aria-label="Fiyat"
        title={hata?.Fiyat || ''}
        disabled={kilitli}
        onChange={(e) => setFiyat(e.target.value)}
        onBlur={() => sayiYaz('Fiyat', fiyat)}
      />

      <Box
        component="select"
        sx={{
          gridArea: 'p',
          ...styles.input(false),
          ...soluk,
          cursor: 'pointer'
        }}
        value={kalem.ParaBirimi}
        aria-label="Para birimi"
        disabled={kilitli}
        onChange={(e) => onDegis(a, 'ParaBirimi', e.target.value)}
      >
        {PARA_BIRIMLERI.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </Box>

      <Box sx={{ gridArea: 's', display: 'grid', placeItems: 'center' }}>
        <Tooltip
          title={
            kalem.Aktif
              ? 'Aktif: tekliflerde seçilebilir'
              : 'Pasif: yeni tekliflerde seçilemez'
          }
          arrow
        >
          <span>
            <Switch
              size="small"
              checked={kalem.Aktif}
              disabled={kilitli}
              onChange={(e) => onDegis(a, 'Aktif', e.target.checked)}
              inputProps={{ 'aria-label': 'Aktif' }}
              sx={{
                '& .Mui-checked': { color: `${BRAND.main} !important` },
                '& .Mui-checked + .MuiSwitch-track': {
                  backgroundColor: `${BRAND.main} !important`
                }
              }}
            />
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ gridArea: 'x' }}>
        {duzenleyebilir && !kalem.KalemId && (
          <Tooltip title="Satırı kaldır" arrow>
            <IconButton
              size="small"
              onClick={() => onSil(a)}
              aria-label="Satırı kaldır"
              sx={{ '&:hover': { color: ERR } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
});

KalemSatiri.propTypes = {
  kalem: PropTypes.object.isRequired,
  durum: PropTypes.string,
  hata: PropTypes.object,
  duzenleyebilir: PropTypes.bool,
  onDegis: PropTypes.func.isRequired,
  onSil: PropTypes.func.isRequired,
  onSurukleBasla: PropTypes.func.isRequired,
  onSurukleUstunde: PropTypes.func.isRequired,
  onBirak: PropTypes.func.isRequired
};

// ==============================|| LİSTE DİYALOĞU ||==============================

function ListeDialog({
  acik,
  mod,
  liste,
  listeler,
  yoneticiMi,
  onKapat,
  onKaydedildi
}) {
  const [ad, setAd] = useState('');
  const [aktif, setAktif] = useState(true);
  const [kaynak, setKaynak] = useState(null);
  const [hata, setHata] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);

  useEffect(() => {
    if (!acik) return;
    setAd(mod === 'duzenle' ? liste?.Ad || '' : '');
    setAktif(mod === 'duzenle' ? liste?.Aktif !== false : true);
    setKaynak(null);
    setHata('');
  }, [acik, mod, liste]);

  const kaydet = () => {
    if (!ad.trim()) {
      setHata('Liste adı boş olamaz.');
      return;
    }
    setKaydediliyor(true);
    fiyatListesiKaydet({
      FiyatListesiId: mod === 'duzenle' ? liste.FiyatListesiId : 0,
      Ad: ad.trim(),
      Aktif: aktif,
      KopyaKaynakId: kaynak ? kaynak.FiyatListesiId : 0
    })
      .then((s) => {
        setKaydediliyor(false);
        if (s.Basarili) onKaydedildi(s.FiyatListesiId, s.Mesaj);
        else setHata(s.Mesaj || 'Kaydedilemedi.');
      })
      .catch(() => {
        setKaydediliyor(false);
        setHata('Kaydedilemedi. Bağlantınızı kontrol edip tekrar deneyin.');
      });
  };

  return (
    <Dialog
      open={acik}
      onClose={onKapat}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: C.ink }}>
        {mod === 'duzenle' ? 'Fiyat listesini düzenle' : 'Yeni fiyat listesi'}
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {hata && <Alert severity="error">{hata}</Alert>}
        {mod === 'yeni' && !yoneticiMi && (
          <Alert severity="info">
            Liste, Apollo&apos;da bağlı olduğunuz servise açılır.
          </Alert>
        )}
        <TextField
          label="Liste adı"
          value={ad}
          onChange={(e) => setAd(e.target.value)}
          inputProps={{ maxLength: 100 }}
          autoFocus
          size="small"
          fullWidth
        />
        {mod === 'yeni' ? (
          <Autocomplete
            filterOptions={trFiltre}
            options={listeler}
            value={kaynak}
            onChange={(e, v) => setKaynak(v)}
            getOptionLabel={(o) => o.Ad || ''}
            isOptionEqualToValue={(o, v) =>
              o.FiyatListesiId === v.FiyatListesiId
            }
            renderInput={(p) => (
              <TextField
                {...p}
                size="small"
                label="Kalemleri kopyala (isteğe bağlı)"
                helperText="Boş bırakılırsa liste boş oluşturulur."
              />
            )}
          />
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={aktif}
                onChange={(e) => setAktif(e.target.checked)}
              />
            }
            label={
              aktif
                ? 'Aktif'
                : 'Pasif: yeni tekliflerde seçilemez, kısıtlı roller göremez'
            }
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
        <Button sx={cancelButton} onClick={onKapat}>
          Vazgeç
        </Button>
        <Button sx={primaryButton} onClick={kaydet} disabled={kaydediliyor}>
          {kaydediliyor ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            'Kaydet'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ListeDialog.propTypes = {
  acik: PropTypes.bool,
  mod: PropTypes.string,
  liste: PropTypes.object,
  listeler: PropTypes.array,
  yoneticiMi: PropTypes.bool,
  onKapat: PropTypes.func,
  onKaydedildi: PropTypes.func
};

// ==============================|| ERİŞİM DİYALOĞU ||==============================

const ERISIM_GOSTERIM_SINIRI = 300;

/** Listenin servis erişim kuralının tek cümlelik özeti. */
const erisimOzeti = (gorebilir, goremez) => {
  if (gorebilir > 0) return `Yalnız ${gorebilir} servis görebilir`;
  if (goremez > 0) return `${goremez} servis dışında tüm servisler görebilir`;
  return 'Tüm servisler görebilir';
};

function ErisimDialog({ acik, liste, onKapat, onKaydedildi }) {
  const [servisler, setServisler] = useState([]);
  // { [ServisId]: true = Görebilir, false = Göremez }
  const [secim, setSecim] = useState({});
  const [ara, setAra] = useState('');
  const [sadeceSecili, setSadeceSecili] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');

  useEffect(() => {
    if (!acik || !liste) return;
    setAra('');
    setHata('');
    setYukleniyor(true);
    Promise.all([getLookuplar(), getFiyatListesiErisim(liste.FiyatListesiId)])
      .then(([l, e]) => {
        // Pasif servis kayıtlıysa da listede adıyla görünsün
        const bilinen = new Set(l.servisler.map((s) => s.Id));
        const ekler = e
          .filter((x) => !bilinen.has(x.ServisId))
          .map((x) => ({
            Id: x.ServisId,
            Dsc: x.ServisAdi || `Servis #${x.ServisId}`
          }));
        setServisler([...l.servisler, ...ekler]);
        setSecim(
          Object.fromEntries(e.map((x) => [x.ServisId, x.Gorebilir === true]))
        );
        setSadeceSecili(e.length > 0);
        setYukleniyor(false);
      })
      .catch(() => {
        setYukleniyor(false);
        setHata('Servisler yüklenemedi.');
      });
  }, [acik, liste]);

  const gorebilirSayi = Object.values(secim).filter((v) => v === true).length;
  const goremezSayi = Object.values(secim).filter((v) => v === false).length;

  const gorunen = useMemo(() => {
    const a = katla(ara.trim());
    return servisler
      .filter(
        (s) =>
          (!sadeceSecili || s.Id in secim) &&
          (!a || katla(s.Dsc).includes(a) || String(s.Id) === ara.trim())
      )
      .sort(
        (x, y) =>
          Number(y.Id in secim) - Number(x.Id in secim) ||
          String(x.Dsc).localeCompare(String(y.Dsc), 'tr')
      );
  }, [servisler, secim, ara, sadeceSecili]);

  // Aynı kutuya tekrar tıklamak seçimi kaldırır
  const isaretle = (id, deger) =>
    setSecim((s) => {
      const y = { ...s };
      if (y[id] === deger) delete y[id];
      else y[id] = deger;
      return y;
    });

  const kaydet = () => {
    setKaydediliyor(true);
    fiyatListesiErisimKaydet(
      liste.FiyatListesiId,
      Object.entries(secim).map(([ServisId, Gorebilir]) => ({
        ServisId: Number(ServisId),
        Gorebilir
      }))
    )
      .then((s) => {
        setKaydediliyor(false);
        if (s.Basarili) onKaydedildi(s.Mesaj);
        else setHata(s.Mesaj || 'Kaydedilemedi.');
      })
      .catch(() => {
        setKaydediliyor(false);
        setHata('Kaydedilemedi. Bağlantınızı kontrol edip tekrar deneyin.');
      });
  };

  return (
    <Dialog
      open={acik}
      onClose={onKapat}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: C.ink, pb: 0.5 }}>
        Erişim: {liste?.Ad}
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: C.inkMuted }}>
          Hiç seçim yoksa liste tüm servislere açıktır. “Görebilir” işaretli
          servis varsa liste yalnız onlara görünür. “Göremez” işaretli servis
          listeyi hiç görmez. Yöneticiler tüm listeleri görür.
        </Typography>
        <Alert
          severity={gorebilirSayi || goremezSayi ? 'info' : 'success'}
          icon={<LockPersonOutlinedIcon fontSize="small" />}
          sx={{ py: 0, alignItems: 'center' }}
          action={
            (gorebilirSayi > 0 || goremezSayi > 0) && (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  setSecim({});
                  setSadeceSecili(false);
                }}
              >
                Temizle
              </Button>
            )
          }
        >
          {erisimOzeti(gorebilirSayi, goremezSayi)}
          {gorebilirSayi > 0 &&
            goremezSayi > 0 &&
            ` · ${goremezSayi} “Göremez” seçimi zaten dışarıda`}
        </Alert>
        {hata && <Alert severity="error">{hata}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Servis ara"
            value={ara}
            onChange={(e) => setAra(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <FormControlLabel
            sx={{ mr: 0, whiteSpace: 'nowrap' }}
            control={
              <Switch
                size="small"
                checked={sadeceSecili}
                onChange={(e) => setSadeceSecili(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Yalnız seçilenler ({gorebilirSayi + goremezSayi})
              </Typography>
            }
          />
        </Box>
        {yukleniyor ? (
          <LinearProgress />
        ) : (
          <TableContainer
            sx={{
              maxHeight: 380,
              border: `1px solid ${C.border}`,
              borderRadius: '10px'
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Servis</TableCell>
                  <TableCell align="center" sx={{ width: 96 }}>
                    Görebilir
                  </TableCell>
                  <TableCell align="center" sx={{ width: 96 }}>
                    Göremez
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gorunen.slice(0, ERISIM_GOSTERIM_SINIRI).map((s) => {
                  const d = secim[s.Id];
                  return (
                    <TableRow key={s.Id} hover>
                      <TableCell>
                        {s.Dsc}{' '}
                        <Typography
                          component="span"
                          sx={{ color: C.inkSoft, fontSize: '0.75rem' }}
                        >
                          #{s.Id}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={d === true}
                          onChange={() => isaretle(s.Id, true)}
                          sx={{ '&.Mui-checked': { color: OK } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={d === false}
                          onChange={() => isaretle(s.Id, false)}
                          sx={{ '&.Mui-checked': { color: ERR } }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {gorunen.length > ERISIM_GOSTERIM_SINIRI && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ color: C.inkMuted }}>
                      {gorunen.length - ERISIM_GOSTERIM_SINIRI} servis daha
                      var; aramayı daraltın.
                    </TableCell>
                  </TableRow>
                )}
                {!gorunen.length && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ color: C.inkMuted }}>
                      {sadeceSecili && !ara
                        ? 'Seçili servis yok; liste tüm servislere açık.'
                        : 'Servis bulunamadı.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
        <Button sx={cancelButton} onClick={onKapat}>
          Vazgeç
        </Button>
        <Button
          sx={primaryButton}
          onClick={kaydet}
          disabled={kaydediliyor || yukleniyor}
        >
          {kaydediliyor ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            'Kaydet'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ErisimDialog.propTypes = {
  acik: PropTypes.bool,
  liste: PropTypes.object,
  onKapat: PropTypes.func,
  onKaydedildi: PropTypes.func
};

// ==============================|| EXCEL YÜKLEME ||==============================

function YuklemeDialog({ acik, kalemler, onKapat, onAktar, onSablon }) {
  const [hata, setHata] = useState('');
  const [onizleme, setOnizleme] = useState(null);
  const [filtre, setFiltre] = useState('tumu');
  const [mod, setMod] = useState('birlestir');
  const [surukleniyor, setSurukleniyor] = useState(false);
  const dosyaRef = useRef(null);

  useEffect(() => {
    if (acik) {
      setHata('');
      setOnizleme(null);
      setFiltre('tumu');
      setMod('birlestir');
    }
  }, [acik]);

  const isle = (dosya) => {
    if (!dosya) return;
    if (!/\.xlsx$/i.test(dosya.name)) {
      setHata('Yalnız Excel dosyası (.xlsx) yüklenebilir.');
      return;
    }
    dosyaOku(dosya)
      .then((aoa) => {
        const okunan = excelOku(aoa);
        if (okunan.genelHata) {
          setHata(okunan.genelHata);
          return;
        }
        setHata('');
        setOnizleme({
          dosyaAdi: dosya.name,
          satirlar: karsilastir(okunan.satirlar, kalemler)
        });
      })
      .catch(() =>
        setHata('Dosya okunamadı. Bozuk olabilir ya da Excel dosyası değil.')
      );
  };

  const pasifler = useMemo(() => {
    if (!onizleme || mod !== 'esitle') return [];
    const dosyada = new Set(
      onizleme.satirlar
        .filter((s) => s.grup && s.tanim)
        .map((s) => kalemAnahtari(s.grup, s.tanim))
    );
    return kalemler.filter(
      (k) => k.Aktif && !dosyada.has(kalemAnahtari(k.Grup, k.Tanim))
    );
  }, [onizleme, mod, kalemler]);

  const say = (d) =>
    onizleme ? onizleme.satirlar.filter((s) => s.durum === d).length : 0;
  const aktarilacak = say('yeni') + say('guncel');

  const ozet = [
    ['tumu', 'Tümü', onizleme?.satirlar.length || 0, C.inkBody],
    ['yeni', 'Yeni', say('yeni'), OK],
    ['guncel', 'Güncellenecek', say('guncel'), BRAND.main],
    ['ayni', 'Değişiklik yok', say('ayni'), C.inkSoft],
    ['hata', 'Hatalı', say('hata'), ERR],
    ...(mod === 'esitle'
      ? [['pasif', 'Pasife alınacak', pasifler.length, '#a16207']]
      : [])
  ];

  const satirlar = onizleme
    ? filtre === 'tumu'
      ? onizleme.satirlar
      : filtre === 'pasif'
        ? []
        : onizleme.satirlar.filter((s) => s.durum === filtre)
    : [];
  const pasifGoster = filtre === 'tumu' || filtre === 'pasif';
  const bolumler = [{ kod: null, ad: 'Ana başlık altında olmayan' }, ...GRUPLAR]
    .map((g) => ({
      ...g,
      satirlar: satirlar.filter((s) => (s.grup || null) === g.kod),
      pasifler: pasifGoster ? pasifler.filter((k) => k.Grup === g.kod) : []
    }))
    .filter((b) => b.satirlar.length + b.pasifler.length);

  return (
    <Dialog
      open={acik}
      onClose={onKapat}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box>
          <Typography
            sx={{ fontWeight: 700, color: C.ink, fontSize: '1.1rem' }}
          >
            Excel&apos;den kalem yükle
          </Typography>
          <Typography variant="body2" sx={{ color: C.inkMuted }}>
            {onizleme
              ? `${onizleme.dosyaAdi} · ${onizleme.satirlar.length} satır okundu`
              : 'Şablona göre doldurulmuş .xlsx dosyasını seçin'}
          </Typography>
        </Box>
        <IconButton onClick={onKapat} aria-label="Kapat">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        {hata && <Alert severity="error">{hata}</Alert>}

        {!onizleme ? (
          <>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setSurukleniyor(true);
              }}
              onDragLeave={() => setSurukleniyor(false)}
              onDrop={(e) => {
                e.preventDefault();
                setSurukleniyor(false);
                isle(e.dataTransfer.files[0]);
              }}
              sx={{
                border: `2px dashed ${surukleniyor ? BRAND.main : C.border}`,
                backgroundColor: surukleniyor ? `${BRAND.main}12` : C.headBg,
                borderRadius: '14px',
                p: 4,
                display: 'grid',
                justifyItems: 'center',
                gap: 1,
                textAlign: 'center'
              }}
            >
              <Box
                sx={{
                  ...styles.secIcon,
                  width: 52,
                  height: 52,
                  borderRadius: '14px'
                }}
              >
                <UploadFileOutlinedIcon />
              </Box>
              <Typography sx={{ fontWeight: 600 }}>
                Excel dosyasını buraya sürükleyin
              </Typography>
              <Typography variant="body2" sx={{ color: C.inkMuted }}>
                veya bilgisayarınızdan seçin · yalnız .xlsx · en fazla 5.000
                satır
              </Typography>
              <Button
                sx={{ ...primaryButton, mt: 1 }}
                onClick={() => dosyaRef.current?.click()}
              >
                Dosya seç
              </Button>
              <input
                ref={dosyaRef}
                type="file"
                accept=".xlsx"
                hidden
                onChange={(e) => {
                  isle(e.target.files[0]);
                  e.target.value = '';
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: C.inkMuted }}>
              Dosya yüklenince hiçbir şey hemen kaydedilmez. Önce önizleme
              gösterilir; aktarılan kalemler ekranda kaydedilmemiş değişiklik
              olarak işaretlenir.
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ozet.map(([k, ad, n, renk]) => (
                <Chip
                  key={k}
                  onClick={() => setFiltre(k)}
                  variant={filtre === k ? 'filled' : 'outlined'}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: renk
                        }}
                      />
                      {ad} <b>{n}</b>
                    </Box>
                  }
                />
              ))}
            </Box>

            <RadioGroup
              row
              value={mod}
              onChange={(e) => {
                setMod(e.target.value);
                if (filtre === 'pasif') setFiltre('tumu');
              }}
              sx={{ gap: 1.5 }}
            >
              {[
                [
                  'birlestir',
                  'Ekle ve güncelle',
                  'Dosyada olmayan kalemlere dokunulmaz.'
                ],
                [
                  'esitle',
                  'Listeyi dosyayla eşitle',
                  'Ek olarak, dosyada olmayan aktif kalemler pasife alınır. Kalem silinmez.'
                ]
              ].map(([v, b, a]) => (
                <FormControlLabel
                  key={v}
                  value={v}
                  control={
                    <Radio sx={{ '&.Mui-checked': { color: BRAND.main } }} />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                      >
                        {b}
                      </Typography>
                      <Typography
                        sx={{ color: C.inkMuted, fontSize: '0.75rem' }}
                      >
                        {a}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    m: 0,
                    pr: 2,
                    flex: '1 1 260px',
                    border: `1px solid ${mod === v ? BRAND.main : C.border}`,
                    backgroundColor:
                      mod === v ? `${BRAND.main}12` : 'transparent',
                    borderRadius: '12px'
                  }}
                />
              ))}
            </RadioGroup>

            <TableContainer
              sx={{
                maxHeight: 420,
                border: `1px solid ${C.border}`,
                borderRadius: '12px'
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={{ width: 64 }}>
                      Satır
                    </TableCell>
                    <TableCell>Malzeme Tanımı</TableCell>
                    <TableCell sx={{ width: 100 }}>Birim</TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>
                      Fiyat
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>Para Birimi</TableCell>
                    <TableCell sx={{ width: 140 }}>Durum</TableCell>
                  </TableRow>
                </TableHead>
                {bolumler.map((b) => (
                  <TableBody key={b.kod || 'yok'}>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ backgroundColor: C.headBg, py: 1 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25
                          }}
                        >
                          <Box
                            sx={{
                              ...styles.secIcon,
                              width: 28,
                              height: 28,
                              borderRadius: '8px',
                              ...(!b.kod && {
                                color: ERR,
                                backgroundColor: '#fdecec'
                              })
                            }}
                          >
                            {b.kod ? (
                              GRUP_IKON[b.kod]
                            ) : (
                              <CloseIcon fontSize="small" />
                            )}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: b.kod ? C.ink : ERR,
                              fontSize: '0.875rem'
                            }}
                          >
                            {b.ad}
                          </Typography>
                          <Chip
                            size="small"
                            label={b.satirlar.length + b.pasifler.length}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                    {b.satirlar.map((s) => {
                      const [ad, bg, fg] = DURUM_ETIKET[s.durum];
                      return (
                        <TableRow key={s.satirNo}>
                          <TableCell align="right" sx={{ color: C.inkMuted }}>
                            {s.satirNo}
                          </TableCell>
                          <TableCell>
                            {s.tanim || (
                              <span style={{ color: C.inkMuted }}>(boş)</span>
                            )}
                            {s.hatalar.length > 0 && (
                              <Typography
                                sx={{ color: ERR, fontSize: '0.75rem' }}
                              >
                                {s.hatalar.join(' ')}
                              </Typography>
                            )}
                            {s.farklar?.length > 0 && (
                              <Typography
                                sx={{ color: C.inkMuted, fontSize: '0.75rem' }}
                              >
                                {s.farklar.map(([alan, eski, yeni]) => (
                                  <span key={alan} style={{ marginRight: 8 }}>
                                    {alan}:{' '}
                                    <s style={{ color: C.inkSoft }}>
                                      {String(eski)}
                                    </s>{' '}
                                    {String(yeni)}
                                  </span>
                                ))}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{s.birim || ''}</TableCell>
                          <TableCell align="right">
                            {s.fiyat != null ? trFormat(s.fiyat) : ''}
                          </TableCell>
                          <TableCell>{s.pb || ''}</TableCell>
                          <TableCell>
                            <Box component="span" sx={styles.pill(bg, fg)}>
                              {ad}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {b.pasifler.map((k) => (
                      <TableRow key={k._anahtar}>
                        <TableCell align="right" sx={{ color: C.inkMuted }}>
                          —
                        </TableCell>
                        <TableCell>
                          {k.Tanim}
                          <Typography
                            sx={{ color: C.inkMuted, fontSize: '0.75rem' }}
                          >
                            Dosyada yok
                          </Typography>
                        </TableCell>
                        <TableCell>{k.Birim}</TableCell>
                        <TableCell align="right">{trFormat(k.Fiyat)}</TableCell>
                        <TableCell>{k.ParaBirimi}</TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={styles.pill(
                              DURUM_ETIKET.pasif[1],
                              DURUM_ETIKET.pasif[2]
                            )}
                          >
                            {DURUM_ETIKET.pasif[0]}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ))}
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: '14px 24px',
          gap: 1,
          flexWrap: 'wrap',
          backgroundColor: C.headBg
        }}
      >
        {!onizleme ? (
          <>
            <Button
              sx={{ ...styles.outlinedButton, mr: 'auto' }}
              variant="outlined"
              onClick={onSablon}
              startIcon={<DescriptionOutlinedIcon />}
            >
              Şablonu indir
            </Button>
            <Button sx={cancelButton} onClick={onKapat}>
              Vazgeç
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: C.inkMuted, mr: 'auto' }}>
              {say('hata')
                ? `${say('hata')} hatalı satır aktarılmayacak. Düzeltip dosyayı yeniden yükleyebilirsiniz.`
                : 'Tüm satırlar geçerli.'}
            </Typography>
            <Button
              sx={styles.outlinedButton}
              variant="outlined"
              onClick={() => setOnizleme(null)}
            >
              Başka dosya seç
            </Button>
            <Button
              sx={primaryButton}
              disabled={!aktarilacak && !pasifler.length}
              onClick={() =>
                onAktar(
                  onizleme.satirlar,
                  pasifler.map((k) => k._anahtar)
                )
              }
            >
              {aktarilacak} kalemi aktar
              {pasifler.length ? `, ${pasifler.length} pasife al` : ''}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

YuklemeDialog.propTypes = {
  acik: PropTypes.bool,
  kalemler: PropTypes.array,
  onKapat: PropTypes.func,
  onAktar: PropTypes.func,
  onSablon: PropTypes.func
};

// ==============================|| EKRAN ||==============================

function HVACPriceLists() {
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kalemYukleniyor, setKalemYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  // Yönetici: rolü T_HAVC_PRICE_LIST_ADMIN_ROLE'da; erişim yalnız ona açık
  const [yoneticiMi, setYoneticiMi] = useState(false);
  const [listeler, setListeler] = useState([]);
  const [seciliId, setSeciliId] = useState(null);
  const [kayitli, setKayitli] = useState([]);
  const [kalemler, setKalemler] = useState([]);
  const [hatalar, setHatalar] = useState({});
  const [ara, setAra] = useState('');
  const [pbFiltre, setPbFiltre] = useState('TUMU');
  const [pasifGoster, setPasifGoster] = useState(false);
  const [kapali, setKapali] = useState({});
  const [bildirim, setBildirim] = useState(null);
  const [listeDialog, setListeDialog] = useState({ acik: false, mod: 'yeni' });
  const [erisimAcik, setErisimAcik] = useState(false);
  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  const [bekleyenListe, setBekleyenListe] = useState(null);
  const surukle = useRef(null);

  const secili = listeler.find((l) => l.FiyatListesiId === seciliId) || null;
  // Sunucu yalnız kullanıcının yönettiği listeleri döner
  const duzenleyebilir = !!secili;

  const bildir = (mesaj, tur = 'success') => setBildirim({ mesaj, tur });

  const listeleriGetir = useCallback(
    (hedefId) =>
      getFiyatListeleri(false)
        .then(({ yoneticiMi: y, listeler: l }) => {
          setYoneticiMi(y);
          setListeler(l);
          setSeciliId((onceki) => {
            const aday = hedefId ?? onceki;
            if (l.some((x) => x.FiyatListesiId === aday)) return aday;
            return l.length ? l[0].FiyatListesiId : null;
          });
        })
        .catch(() => bildir('Fiyat listeleri yüklenemedi.', 'error')),
    []
  );

  const kalemleriGetir = useCallback((id) => {
    if (!id) {
      setKayitli([]);
      setKalemler([]);
      return Promise.resolve();
    }
    setKalemYukleniyor(true);
    return getFiyatListesiKalemleri(id, false)
      .then((data) => {
        const n = data.map(normalize);
        setKayitli(n);
        setKalemler(n);
        setHatalar({});
        setKalemYukleniyor(false);
      })
      .catch(() => {
        setKalemYukleniyor(false);
        bildir('Kalemler yüklenemedi.', 'error');
      });
  }, []);

  useEffect(() => {
    listeleriGetir().finally(() => setYukleniyor(false));
  }, [listeleriGetir]);

  useEffect(() => {
    kalemleriGetir(seciliId);
  }, [seciliId, kalemleriGetir]);

  // ---------------------------------------------------- değişiklik takibi
  const kayitliHarita = useMemo(
    () => new Map(kayitli.map((k) => [k._anahtar, k])),
    [kayitli]
  );

  const durumlar = useMemo(() => {
    const d = {};
    kalemler.forEach((k) => {
      const o = kayitliHarita.get(k._anahtar);
      if (!o) d[k._anahtar] = 'yeni';
      else if (ALANLAR.some((a) => o[a] !== k[a])) d[k._anahtar] = 'degisti';
    });
    return d;
  }, [kalemler, kayitliHarita]);

  const siraDegisti = useMemo(() => {
    const a = sirala(kayitli)
      .map((k) => k._anahtar)
      .join();
    const b = kalemler
      .filter((k) => kayitliHarita.has(k._anahtar))
      .map((k) => k._anahtar)
      .join();
    return a !== b;
  }, [kayitli, kalemler, kayitliHarita]);

  const degisiklik = useMemo(() => {
    const v = Object.values(durumlar);
    const yeni = v.filter((x) => x === 'yeni').length;
    const degisen = v.filter((x) => x === 'degisti').length;
    return { yeni, degisen, var: yeni + degisen > 0 || siraDegisti };
  }, [durumlar, siraDegisti]);

  useEffect(() => {
    if (!degisiklik.var) return undefined;
    const uyar = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', uyar);
    return () => window.removeEventListener('beforeunload', uyar);
  }, [degisiklik.var]);

  // ---------------------------------------------------- satır işlemleri
  const onDegis = useCallback((anahtar, alan, deger) => {
    setKalemler((l) =>
      l.map((k) => (k._anahtar === anahtar ? { ...k, [alan]: deger } : k))
    );
    setHatalar((h) => {
      if (!h[anahtar]?.[alan]) return h;
      const y = { ...h, [anahtar]: { ...h[anahtar] } };
      delete y[anahtar][alan];
      return y;
    });
  }, []);

  const onSil = useCallback(
    (anahtar) => setKalemler((l) => l.filter((k) => k._anahtar !== anahtar)),
    []
  );

  const kalemEkle = (grup) => {
    const yeni = {
      _anahtar: yeniAnahtar(),
      KalemId: 0,
      Grup: grup,
      Tanim: '',
      Adet: 1,
      Birim: 'Adet',
      Fiyat: '',
      ParaBirimi: 'TRY',
      Aktif: true,
      Sira: 0
    };
    setKapali((k) => ({ ...k, [grup]: false }));
    setAra('');
    setPbFiltre('TUMU');
    setKalemler((l) => sirala([...l, yeni]));
  };

  const onSurukleBasla = useCallback((e, kalem) => {
    surukle.current = kalem;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', kalem._anahtar);
  }, []);

  const onSurukleUstunde = useCallback((e, hedef) => {
    if (surukle.current && surukle.current.Grup === hedef.Grup)
      e.preventDefault();
  }, []);

  const onBirak = useCallback((e, hedef) => {
    const kaynak = surukle.current;
    surukle.current = null;
    if (
      !kaynak ||
      kaynak._anahtar === hedef._anahtar ||
      kaynak.Grup !== hedef.Grup
    )
      return;
    e.preventDefault();
    setKalemler((l) => {
      const kalan = l.filter((k) => k._anahtar !== kaynak._anahtar);
      const i = kalan.findIndex((k) => k._anahtar === hedef._anahtar);
      kalan.splice(
        i,
        0,
        l.find((k) => k._anahtar === kaynak._anahtar)
      );
      return kalan;
    });
  }, []);

  // ---------------------------------------------------- kaydet
  const dogrula = () => {
    const h = {};
    const gorulen = new Set();
    kalemler.forEach((k) => {
      const e = {};
      if (!String(k.Tanim).trim()) e.Tanim = 'Tanım zorunlu.';
      else {
        const a = kalemAnahtari(k.Grup, k.Tanim);
        if (gorulen.has(a)) e.Tanim = 'Bu başlıkta aynı tanım zaten var.';
        else gorulen.add(a);
      }
      if (typeof k.Adet !== 'number' || !(k.Adet > 0))
        e.Adet = "Adet 0'dan büyük olmalı.";
      if (typeof k.Fiyat !== 'number' || k.Fiyat < 0)
        e.Fiyat = 'Geçerli bir fiyat girin.';
      if (Object.keys(e).length) h[k._anahtar] = e;
    });
    return h;
  };

  const kaydet = () => {
    const h = dogrula();
    if (Object.keys(h).length) {
      setHatalar(h);
      setAra('');
      setPbFiltre('TUMU');
      setPasifGoster(true);
      setKapali({});
      bildir(
        `${Object.keys(h).length} satırda eksik veya hatalı bilgi var. İşaretli alanları düzeltin.`,
        'error'
      );
      return;
    }
    const siralar = {};
    const gonderilecek = kalemler.map((k) => {
      siralar[k.Grup] = (siralar[k.Grup] || 0) + 10;
      return {
        ...k,
        Fiyat: Math.round(k.Fiyat * 100) / 100,
        Sira: siralar[k.Grup]
      };
    });
    setKaydediliyor(true);
    fiyatListesiKalemleriKaydet(seciliId, gonderilecek)
      .then((s) => {
        setKaydediliyor(false);
        if (!s.Basarili) {
          bildir(s.Mesaj || 'Kaydedilemedi.', 'error');
          return;
        }
        bildir(
          `Kaydedildi: ${s.Eklenen || 0} yeni, ${s.Guncellenen || 0} güncellenen kalem.`
        );
        kalemleriGetir(seciliId);
        listeleriGetir(seciliId);
      })
      .catch(() => {
        setKaydediliyor(false);
        bildir(
          'Kaydedilemedi. Bağlantınızı kontrol edip tekrar deneyin.',
          'error'
        );
      });
  };

  const vazgec = () => {
    setKalemler(kayitli);
    setHatalar({});
  };

  // ---------------------------------------------------- liste değiştirme
  const listeSec = (id) => {
    if (id === seciliId) return;
    if (degisiklik.var) setBekleyenListe(id);
    else setSeciliId(id);
  };

  // ---------------------------------------------------- excel
  const sablonIndir = () =>
    excelOlustur([])
      .then((b) => blobIndir(b, 'TeklifKalemleri_Sablon.xlsx'))
      .catch(() => bildir('Şablon oluşturulamadı.', 'error'));

  const disaAktar = () => {
    const tarih = new Date().toISOString().slice(0, 10);
    excelOlustur(sirala(kayitli).filter((k) => k.Aktif))
      .then((b) => blobIndir(b, `${dosyaAdiTemizle(secili?.Ad)}_${tarih}.xlsx`))
      .catch(() => bildir('Excel dosyası oluşturulamadı.', 'error'));
  };

  const aktar = (satirlar, pasifAnahtarlar) => {
    const harita = new Map(
      kalemler.map((k) => [kalemAnahtari(k.Grup, k.Tanim), k._anahtar])
    );
    const pasif = new Set(pasifAnahtarlar);
    let n = 0;
    const eklenecek = [];
    const guncel = new Map();
    satirlar.forEach((s) => {
      if (s.durum === 'yeni') {
        n += 1;
        eklenecek.push({
          _anahtar: yeniAnahtar(),
          KalemId: 0,
          Grup: s.grup,
          Tanim: s.tanim,
          Adet: 1,
          Birim: s.birim,
          Fiyat: s.fiyat,
          ParaBirimi: s.pb,
          Aktif: true,
          Sira: 0
        });
      } else if (s.durum === 'guncel') {
        n += 1;
        guncel.set(harita.get(kalemAnahtari(s.grup, s.tanim)), s);
      }
    });
    setKalemler((l) =>
      sirala([
        ...l.map((k) => {
          const s = guncel.get(k._anahtar);
          if (s)
            return {
              ...k,
              Birim: s.birim,
              Fiyat: s.fiyat,
              ParaBirimi: s.pb,
              Aktif: true
            };
          if (pasif.has(k._anahtar)) return { ...k, Aktif: false };
          return k;
        }),
        ...eklenecek
      ])
    );
    setYuklemeAcik(false);
    setAra('');
    setPbFiltre('TUMU');
    setKapali({});
    bildir(
      `${n} kalem aktarıldı${pasif.size ? `, ${pasif.size} kalem pasife alındı` : ''}. Kalıcı olması için Kaydet'e basın.`,
      'info'
    );
  };

  // ---------------------------------------------------- görünüm
  const gorunurMu = (k) => {
    if (
      !pasifGoster &&
      !k.Aktif &&
      !durumlar[k._anahtar] &&
      !hatalar[k._anahtar]
    )
      return false;
    if (pbFiltre !== 'TUMU' && k.ParaBirimi !== pbFiltre) return false;
    if (ara && !katla(k.Tanim).includes(katla(ara))) return false;
    return true;
  };
  const aktifSayi = kalemler.filter((k) => k.Aktif).length;

  if (yukleniyor) {
    return (
      <Page sx={styles.root} title="Teklif Fiyat Listeleri">
        <LinearProgress />
      </Page>
    );
  }

  return (
    <Page sx={styles.root} title="Teklif Fiyat Listeleri">
      <Box sx={styles.hero}>
        <Box sx={styles.heroIcon}>
          <RequestQuoteIcon />
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{ color: C.ink, fontWeight: 700, letterSpacing: '-0.5px' }}
          >
            Teklif Fiyat Listeleri
          </Typography>
          <Typography variant="body2" sx={{ color: C.inkMuted }}>
            Tekliflerde seçilecek malzeme, işçilik ve diğer bedellerin listeleri
          </Typography>
        </Box>
      </Box>

      <Card sx={styles.card} elevation={0}>
        {(kalemYukleniyor || kaydediliyor) && (
          <LinearProgress
            sx={{ '& .MuiLinearProgress-bar': { backgroundColor: BRAND.main } }}
          />
        )}

        <Box sx={styles.listeBar}>
          <Autocomplete
            filterOptions={trFiltre}
            sx={{ flex: '1 1 320px', maxWidth: 480 }}
            options={listeler}
            value={secili}
            disableClearable={!!secili}
            onChange={(e, v) => v && listeSec(v.FiyatListesiId)}
            getOptionLabel={(o) => o.Ad || ''}
            isOptionEqualToValue={(o, v) =>
              o.FiyatListesiId === v.FiyatListesiId
            }
            noOptionsText="Liste yok"
            renderOption={({ key: _k, ...p }, o) => (
              <li {...p} key={o.FiyatListesiId}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%'
                  }}
                >
                  <span style={{ flex: 1 }}>{o.Ad}</span>
                  {!o.Aktif && <Chip size="small" label="Pasif" />}
                  <Typography variant="caption" sx={{ color: C.inkMuted }}>
                    {o.KalemSayisi} kalem
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(p) => (
              <TextField {...p} size="small" label="Fiyat listesi" />
            )}
          />
          {secili && !secili.Aktif && (
            <Chip label="Pasif liste" sx={styles.pill('#fef6dc', '#a16207')} />
          )}
          {secili && yoneticiMi && (
            <Tooltip title="Servis erişimini düzenle" arrow>
              <Chip
                icon={<LockPersonOutlinedIcon />}
                label={erisimOzeti(
                  secili.GorebilirServisSayisi,
                  secili.GoremezServisSayisi
                )}
                onClick={() => setErisimAcik(true)}
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {secili && (
            <>
              {yoneticiMi && (
                <Button
                  variant="outlined"
                  sx={styles.outlinedButton}
                  startIcon={<LockPersonOutlinedIcon />}
                  onClick={() => setErisimAcik(true)}
                >
                  Erişim
                </Button>
              )}
              <Button
                variant="outlined"
                sx={styles.outlinedButton}
                startIcon={<EditOutlinedIcon />}
                onClick={() => setListeDialog({ acik: true, mod: 'duzenle' })}
              >
                Düzenle
              </Button>
            </>
          )}
          <Button
            sx={neutralButton}
            startIcon={<AddIcon />}
            onClick={() => setListeDialog({ acik: true, mod: 'yeni' })}
          >
            Yeni Liste
          </Button>
        </Box>

        {!secili ? (
          <Box sx={{ p: 5, textAlign: 'center', color: C.inkMuted }}>
            <Typography sx={{ fontWeight: 600, color: C.inkBody, mb: 0.5 }}>
              {yoneticiMi
                ? 'Henüz fiyat listesi yok'
                : 'Servisinize ait fiyat listesi yok'}
            </Typography>
            <Typography variant="body2">
              &quot;Yeni Liste&quot; ile ilk listeyi oluşturun, ardından kalemleri ekleyin veya Excel&apos;den yükleyin.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={styles.toolbar}>
              <TextField
                size="small"
                placeholder="Kalem ara"
                value={ara}
                onChange={(e) => setAra(e.target.value)}
                sx={{ flex: '1 1 240px', maxWidth: 360 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={pbFiltre}
                onChange={(e, v) => v && setPbFiltre(v)}
              >
                {['TUMU', ...PARA_BIRIMLERI].map((p) => (
                  <ToggleButton
                    key={p}
                    value={p}
                    sx={{ textTransform: 'none', px: 1.5 }}
                  >
                    {p === 'TUMU' ? 'Tümü' : p}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={pasifGoster}
                    onChange={(e) => setPasifGoster(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Pasif kalemleri göster
                  </Typography>
                }
              />
              <Typography
                variant="body2"
                sx={{ color: C.inkMuted, ml: { md: 'auto' } }}
              >
                {aktifSayi} aktif · {kalemler.length - aktifSayi} pasif
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  sx={styles.outlinedButton}
                  startIcon={<DescriptionOutlinedIcon />}
                  onClick={sablonIndir}
                >
                  Şablon
                </Button>
                <Button
                  variant="outlined"
                  sx={styles.outlinedButton}
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={disaAktar}
                >
                  Excel&apos;e Aktar
                </Button>
                {duzenleyebilir && (
                  <Button
                    sx={primaryButton}
                    startIcon={<FileUploadOutlinedIcon />}
                    onClick={() => setYuklemeAcik(true)}
                  >
                    Excel&apos;den Yükle
                  </Button>
                )}
              </Box>
            </Box>

            <Box sx={styles.colHead} aria-hidden="true">
              <span />
              <span>Malzeme Tanımı</span>
              <span style={{ textAlign: 'right' }}>Adet</span>
              <span>Birim</span>
              <span style={{ textAlign: 'right' }}>Fiyat</span>
              <span>Para Birimi</span>
              <span style={{ textAlign: 'center' }}>Aktif</span>
              <span />
            </Box>

            <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'grid', gap: 1.75 }}>
              {GRUPLAR.map((g) => {
                const hepsi = kalemler.filter((k) => k.Grup === g.kod);
                const gorunen = hepsi.filter(gorunurMu);
                const pasifSay = hepsi.filter((k) => !k.Aktif).length;
                const acik = !kapali[g.kod];
                return (
                  <Box key={g.kod} sx={styles.section}>
                    <Box sx={styles.secHead}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setKapali((k) => ({ ...k, [g.kod]: acik }))
                        }
                        aria-expanded={acik}
                        aria-label={`${g.ad} grubunu ${acik ? 'kapat' : 'aç'}`}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: acik ? 'none' : 'rotate(-90deg)',
                            transition: 'transform .15s'
                          }}
                        />
                      </IconButton>
                      <Box sx={styles.secIcon}>{GRUP_IKON[g.kod]}</Box>
                      <Typography sx={{ fontWeight: 600, color: C.ink }}>
                        {g.ad}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${hepsi.length - pasifSay} aktif${pasifSay ? ` · ${pasifSay} pasif` : ''}`}
                      />
                      <Box sx={{ flex: 1 }} />
                      {duzenleyebilir && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ...styles.outlinedButton, height: 32 }}
                          startIcon={<AddIcon />}
                          onClick={() => kalemEkle(g.kod)}
                        >
                          Kalem Ekle
                        </Button>
                      )}
                    </Box>
                    {acik &&
                      (gorunen.length ? (
                        gorunen.map((k) => (
                          <KalemSatiri
                            key={k._anahtar}
                            kalem={k}
                            durum={durumlar[k._anahtar]}
                            hata={hatalar[k._anahtar]}
                            duzenleyebilir={duzenleyebilir}
                            onDegis={onDegis}
                            onSil={onSil}
                            onSurukleBasla={onSurukleBasla}
                            onSurukleUstunde={onSurukleUstunde}
                            onBirak={onBirak}
                          />
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            px: 2,
                            py: 2,
                            color: C.inkMuted,
                            borderTop: `1px solid ${C.borderSoft}`
                          }}
                        >
                          {hepsi.length
                            ? 'Filtreye uyan kalem yok.'
                            : duzenleyebilir
                              ? 'Bu başlıkta henüz kalem yok. "Kalem Ekle" ile ya da Excel\'den yükleyerek ekleyin.'
                              : 'Bu başlıkta kalem yok.'}
                        </Typography>
                      ))}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Card>

      {duzenleyebilir && degisiklik.var && (
        <Box sx={styles.saveBar}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#ffffff' }}
            >
              Kaydedilmemiş değişiklikler
            </Typography>
            <Typography
              sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.72)' }}
            >
              {[
                degisiklik.yeni && `${degisiklik.yeni} yeni`,
                degisiklik.degisen && `${degisiklik.degisen} değişen`,
                siraDegisti && 'sıra değişti'
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          <Button
            sx={{
              color: '#fff !important',
              textTransform: 'none',
              fontWeight: 600
            }}
            onClick={vazgec}
            disabled={kaydediliyor}
          >
            Vazgeç
          </Button>
          <Button sx={primaryButton} onClick={kaydet} disabled={kaydediliyor}>
            {kaydediliyor ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              'Kaydet'
            )}
          </Button>
        </Box>
      )}

      <ListeDialog
        acik={listeDialog.acik}
        mod={listeDialog.mod}
        liste={secili}
        listeler={listeler}
        yoneticiMi={yoneticiMi}
        onKapat={() => setListeDialog((d) => ({ ...d, acik: false }))}
        onKaydedildi={(id, mesaj) => {
          setListeDialog((d) => ({ ...d, acik: false }));
          bildir(mesaj || 'Fiyat listesi kaydedildi.');
          listeleriGetir(id);
        }}
      />

      <ErisimDialog
        acik={erisimAcik}
        liste={secili}
        onKapat={() => setErisimAcik(false)}
        onKaydedildi={(mesaj) => {
          setErisimAcik(false);
          bildir(mesaj || 'Erişimler kaydedildi.');
          listeleriGetir();
        }}
      />

      <YuklemeDialog
        acik={yuklemeAcik}
        kalemler={kalemler}
        onKapat={() => setYuklemeAcik(false)}
        onAktar={aktar}
        onSablon={sablonIndir}
      />

      <Dialog
        open={bekleyenListe !== null}
        onClose={() => setBekleyenListe(null)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: C.ink }}>
          Kaydedilmemiş değişiklikler var
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: C.inkBody }}>
            Başka listeye geçerseniz bu listedeki kaydedilmemiş değişiklikler
            kaybolur.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
          <Button sx={cancelButton} onClick={() => setBekleyenListe(null)}>
            Bu listede kal
          </Button>
          <Button
            sx={primaryButton}
            onClick={() => {
              setSeciliId(bekleyenListe);
              setBekleyenListe(null);
            }}
          >
            Değişiklikleri at ve geç
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!bildirim}
        autoHideDuration={4500}
        onClose={() => setBildirim(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {bildirim ? (
          <Alert
            severity={bildirim.tur}
            variant="filled"
            onClose={() => setBildirim(null)}
          >
            {bildirim.mesaj}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Page>
  );
}

export default HVACPriceLists;
